/* =====================================================================
 * NotImplemented stub providers.
 *
 * These factories accept their full typed config so a tenant can
 * already model a SAML / OIDC / email+password login in their
 * manifest, but every runtime hook throws `NotImplementedError`
 * until the real adapter ships.
 *
 * Keeping the surface now (rather than waiting for Phase 10) means:
 *   - TypeScript discriminates correctly across the full provider
 *     union, so UI code written today won't need to grow
 *     conditionals when a real adapter replaces the stub.
 *   - On-premise demos can render the button list (disabled state)
 *     without forking `@auw/auth`, which previews the final IA
 *     without making promises the backend can't keep.
 * ===================================================================== */

import {
  NotImplementedError,
  type AuthProvider,
  type EmailPasswordProviderConfig,
  type KnoxProviderConfig,
  type OidcProviderConfig,
  type SamlProviderConfig,
} from '../types';

function stubProvider<
  TKind extends 'saml' | 'oidc' | 'email_password' | 'knox',
>(args: {
  id: string;
  kind: TKind;
  labelKey: string;
  iconSrc?: string;
}): AuthProvider {
  const { id, kind, labelKey, iconSrc } = args;
  return {
    id,
    kind,
    labelKey,
    iconSrc,
    startLogin() {
      throw new NotImplementedError(kind, id);
    },
    handleCallback() {
      return Promise.reject(new NotImplementedError(kind, id));
    },
  };
}

/** SAML stub — `SamlProviderConfig` is accepted so the tenant
 *  manifest shape is already settled. */
export function samlProvider(config: SamlProviderConfig): AuthProvider {
  return stubProvider({
    id: config.id,
    kind: 'saml',
    labelKey: config.labelKey ?? 'auth.login.continueWithSaml',
    iconSrc: config.iconSrc,
  });
}

/** OIDC stub. */
export function oidcProvider(config: OidcProviderConfig): AuthProvider {
  return stubProvider({
    id: config.id,
    kind: 'oidc',
    labelKey: config.labelKey ?? 'auth.login.continueWithOidc',
    iconSrc: config.iconSrc,
  });
}

/** Email+password stub. */
export function emailPasswordProvider(
  config: EmailPasswordProviderConfig,
): AuthProvider {
  return stubProvider({
    id: config.id,
    kind: 'email_password',
    labelKey: config.labelKey ?? 'auth.login.emailPassword',
    iconSrc: config.iconSrc,
  });
}

/** Knox stub. Enterprise apps should supply a tenant-specific adapter. */
export function knoxProvider(config: KnoxProviderConfig): AuthProvider {
  return stubProvider({
    id: config.id,
    kind: 'knox',
    labelKey: config.labelKey ?? 'auth.login.continueWithKnox',
    iconSrc: config.iconSrc,
  });
}
